/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import ExpandOutlinedIcon from "@mui/icons-material/ExpandOutlined";
import ReadMoreOutlinedIcon from "@mui/icons-material/ReadMoreOutlined";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  getTimeSeriesMetricsQuery,
  getAlarmHistoryQuery,
  getActivityGraphConfigQuery,
  putActivityGraphConfig,
  getTouIntervalsQuery,
  getWeatherHistoricalQuery,
  } from "../../lib/Queries";
  import useUserStore from "../../state/UserStore";
import { useSnackbar } from "notistack";
import {
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Line,
  ReferenceArea,
  ComposedChart,
} from "recharts";
import LineGraphTooltip from "../Graphs/Common/LineGraphTooltip";
import Loading from "../Shared/Loading";
import TimeSeriesError from "../Graphs/Common/TimeSeriesError";
import DateOptionPicker from "../Graphs/Common/DateOptionPicker";
import { decorateAlarms, getUnixTimeMs } from "../../lib/Util";
import GraphUtil from "../../lib/GraphUtil";
import AlarmTimeline from "../Graphs/Common/AlarmTimeline";
import TouTimeline from "../Graphs/Common/TouTimeline";
import WeatherTimeline from "../Graphs/Common/WeatherTimeline";
import { weatherConfig } from "../Graphs/Common/weatherConfig";
import { generateGetWeatherTooltipItems } from "../../lib/weatherUtils";
import {
  generateGetActiveTouItem,
  generateStaticTouItems,
} from "../../lib/touUtils";
import CustomTick from "../Graphs/Common/CustomTick";
import CustomXAxis from "../Graphs/Common/CustomXAxis";
import { findSeries, findAssembly, getMetricAxisId } from "../Graphs/Logic/formatUtils";
import GraphToggle from "../Graphs/Common/GraphToggle";
import GraphDownloadMenu from "../Graphs/Common/GraphDownloadMenu";
import ConfigureDataModal from "./ConfigureDataModal";
import {
  SiteMetadata,
  GraphRange,
  AxisUnit,
  ZoomParams,
  BigBucket,
  TimeSeriesRow,
} from "../../lib/Types";

const TRACE_GROUP_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Solar", label: "Solar" },
  { value: "BESS", label: "Batteries" },
  { value: "Genset", label: "Gensets" },
];

const getAvailableTraceGroups = (site: SiteMetadata) => {
  const presentTypes = new Set(
    site.assemblies.map((assembly) => assembly.scaleos_type).filter(Boolean),
  );
  return TRACE_GROUP_OPTIONS.filter(
    (opt) => opt.value === "All" || presentTypes.has(opt.value),
  );
};

const getDefaultAssetType = (
  availableTraceGroups: typeof TRACE_GROUP_OPTIONS,
): string => {
  const availableValues = new Set(availableTraceGroups.map((opt) => opt.value));
  if (availableValues.has("BESS")) return "BESS";
  if (availableValues.has("Solar")) return "Solar";
  if (availableValues.has("Genset")) return "Genset";
  return "All";
};

const getTraceGroupLabel = (value: string): string => {
  const option = TRACE_GROUP_OPTIONS.find((opt) => opt.value === value);
  return option?.label ?? value;
};

interface ActivityTabProps {
  site: SiteMetadata;
}

const ActivityTab: React.FC<ActivityTabProps> = ({ site }) => {
  const theme = useTheme();
  const flags = useFlags();
  const userStore = useUserStore();
  const isAdmin = userStore.isAdmin();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Asset type filter
  const availableTraceGroups = useMemo(
    () => getAvailableTraceGroups(site),
    [site.assemblies],
  );
  const [selectedAssetType, setSelectedAssetType] = useState(() =>
    getDefaultAssetType(getAvailableTraceGroups(site)),
  );

  useEffect(() => {
    setSelectedAssetType((current) => {
      const availableValues = new Set(
        availableTraceGroups.map((opt) => opt.value),
      );
      if (availableValues.has(current)) return current;
      return getDefaultAssetType(availableTraceGroups);
    });
  }, [availableTraceGroups]);

  // Configured metrics per asset type
  const [configuredMetrics, setConfiguredMetrics] = useState<
    Record<string, string[]>
  >({});

  // Modal metrics (temporary state while modal is open)
  const [modalMetrics, setModalMetrics] = useState<string[]>([]);

  // Settings modal
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Graph state
  const [timeSeries, setTimeSeries] = useState<TimeSeriesRow[]>([]);
  const timezone = site.timezone ?? "UTC";
  const [timeseriesDataKeys, setTimeseriesDataKeys] = useState<string[]>([]);
  const [animation, setAnimation] = useState(true);
  const [animationCount, setAnimationCount] = useState(0);
  const [bigBuckets, setBigBuckets] = useState<BigBucket[]>([]);
  const [zoomParams, setZoomParams] = useState<ZoomParams>({ zoomed: false });
  const [preZoomDisplayRange, setPreZoomDisplayRange] =
    useState<GraphRange | null>(null);
  const [axisUnits, setAxisUnits] = useState<AxisUnit[]>([]);
  const [useGraphInterpolation, setUseGraphInterpolation] = useState(true);
  const [includeZeroYAxis, setIncludeZeroYAxis] = useState(false);
  const [displayRange, setDisplayRange] = useState<GraphRange>(
    GraphUtil.getInitial24HourGraphRange(timezone),
  );
  const [graphPayload, setGraphPayload] = useState<unknown>(null);
  const [lockLegend, setLockLegend] = useState(false);

  // Fetch saved activity graph config
  const { data: savedConfig } = useQuery({
    ...getActivityGraphConfigQuery(site.uuid),
    enabled: !!site.uuid,
    retry: false,
  });

  useEffect(() => {
    if (savedConfig) {
      setConfiguredMetrics(savedConfig.trace_group_metrics ?? {});
      setUseGraphInterpolation(savedConfig.interpolation ?? true);
      setIncludeZeroYAxis(savedConfig.y_axis_adjust ?? false);
      setLockLegend(savedConfig.lock_legend ?? false);
    }
  }, [savedConfig]);

  // Save mutation
  const saveConfigMutation = useMutation({
    mutationFn: (payload: {
      trace_group_metrics: Record<string, string[]>;
      interpolation: boolean;
      y_axis_adjust: boolean;
      lock_legend: boolean;
    }) => putActivityGraphConfig(site.uuid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activityGraphConfig", site.uuid] });
      enqueueSnackbar("Graph saved.", { autoHideDuration: 5000 });
      setSettingsOpen(false);
    },
    onError: () => {
      enqueueSnackbar(
        "Graph unable to save. Please try again in a few minutes.",
        { autoHideDuration: 5000 },
      );
    },
  });

  const metricUuids = useMemo(() => {
    if (selectedAssetType === "All") {
      const allUuids = new Set<string>();
      Object.values(configuredMetrics).forEach((uuids) =>
        uuids.forEach((uuid) => allUuids.add(uuid)),
      );
      return Array.from(allUuids);
    }
    return configuredMetrics[selectedAssetType] ?? [];
  }, [selectedAssetType, configuredMetrics]);

  // No default metrics — user must configure via the settings modal

  // Data fetching
  const {
    isFetching,
    error,
    data: metricsTimeseriesData,
    refetch,
    isRefetchError,
  } = useQuery({
    ...getTimeSeriesMetricsQuery(
      site.uuid,
      displayRange.start,
      displayRange.stop,
      displayRange.interval,
      metricUuids,
    ),
    enabled: metricUuids?.length > 0,
    staleTime: 5000,
  });

  const { data: alarmHistoryData, refetch: refetchAlarms } = useQuery({
    ...getAlarmHistoryQuery(site.uuid, displayRange.start, displayRange.stop),
  });

  const { data: touData } = useQuery({
    ...getTouIntervalsQuery(site.uuid, displayRange.start, displayRange.stop),
  });

  const { data: weatherTimeSeriesData } = useQuery({
    ...getWeatherHistoricalQuery(site.uuid, displayRange.start, displayRange.stop),
  });

  useEffect(() => {
    if (alarmHistoryData) {
      decorateAlarms(alarmHistoryData.historical_alerts, site.assemblies);
    }
  }, [alarmHistoryData, site.assemblies]);

  useEffect(() => {
    if (metricUuids?.length > 0) {
      refetch();
    }
    refetchAlarms();
  }, [displayRange]);

  useEffect(() => {
    if (!metricUuids?.length) {
      setTimeSeries([]);
    }
  }, [metricUuids]);

  const handleAnimationEnd = () => {
    setAnimationCount((prev) => prev + 1);
    if (animationCount >= 0) {
      setAnimation(false);
    }
  };

  const sortTimeSeriesDataKeys = (
    keys: string[],
    uuids: string[],
  ): string[] => {
    const sorted: string[] = [];
    uuids.forEach((uuid) => {
      if (keys.includes(uuid)) sorted.push(uuid);
    });
    return sorted;
  };

  const seriesIsAllNull = (ts: TimeSeriesRow[], metricUuid: string): boolean => {
    if (!ts) return true;
    return ts.filter((row) => row[metricUuid] === null).length === ts.length;
  };

  const setupData = () => {
    if (!metricsTimeseriesData?.time_series?.length) {
      setTimeseriesDataKeys([]);
      setTimeSeries([]);
      return;
    }

    const rawKeys = Object.keys(metricsTimeseriesData.time_series[0]).filter(
      (key) =>
        !key.endsWith("_min") && !key.endsWith("_max") && key !== "bucket",
    );
    const sortedKeys = sortTimeSeriesDataKeys(rawKeys, metricUuids);
    setTimeseriesDataKeys(sortedKeys);

    const seriesData = structuredClone(metricsTimeseriesData.time_series);
    seriesData.forEach((row: TimeSeriesRow) => {
      row.unixTime = getUnixTimeMs(row.bucket, timezone);
    });
    const { timeSeries: dataWithBigBuckets, newBigBuckets } =
      GraphUtil.calculateBigBuckets(seriesData);

    setBigBuckets(newBigBuckets);

    const newAxes: AxisUnit[] = [];
    sortedKeys.forEach((metric) => {
      const result = findSeries(metric, site);
      if (!result?.tsMetric) return;
      const axisId = getMetricAxisId(result.tsMetric);
      newAxes.push({ unit: axisId, id: axisId });
    });
    setAxisUnits(newAxes);
    setTimeSeries(dataWithBigBuckets);
  };

  useEffect(() => {
    setupData();
  }, [metricsTimeseriesData]);

  const getBucketAlarms = GraphUtil.generateGetBucketAlarms(
    bigBuckets,
    alarmHistoryData,
  );

  const getWeatherTooltipItems = useMemo(
    () =>
      weatherTimeSeriesData
        ? generateGetWeatherTooltipItems(weatherConfig, weatherTimeSeriesData)
        : undefined,
    [weatherTimeSeriesData],
  );

  const getActiveTouItem = useMemo(
    () => generateGetActiveTouItem(touData?.results ?? [], displayRange),
    [touData, displayRange],
  );
  const staticTouItems = useMemo(
    () => generateStaticTouItems(touData?.results ?? [], displayRange),
    [touData, displayRange],
  );

  const handleWeatherHover = (ev: { activeLabel?: string | number }) => {
    if (ev?.activeLabel == null) return;
    const synthetic = GraphUtil.buildSyntheticGraphPayload(
      timeSeries,
      Number(ev.activeLabel),
      (row: TimeSeriesRow) =>
        timeseriesDataKeys
          .filter((key) => !seriesIsAllNull(timeSeries, key))
          .map((key) => ({
            name: key,
            value: row[key],
            color: (theme as any).recharts["grapherColors"][
              timeseriesDataKeys.indexOf(key)
            ],
            payload: row,
          })),
    );
    if (synthetic) setGraphPayload(synthetic);
  };

  const refreshGraph = () => {
    setDisplayRange(GraphUtil.getInitial24HourGraphRange(timezone));
    setZoomParams({ zoomed: false });
    refetch();
    refetchAlarms();
  };

  const cancelZoom = () => {
    setZoomParams({ zooming: false, zoomed: false });
    setDisplayRange(GraphUtil.updateDisplayRange(preZoomDisplayRange));
  };

  const singleYAxisWidth = 70;

  const getYAxes = () => {
    const reversedKeys = [...timeseriesDataKeys].reverse();
    const usedUnits: string[] = [];
    const axes: React.ReactNode[] = [];
    reversedKeys.forEach((metricUuid) => {
      const series_response = findSeries(metricUuid, site);
      const tsMetric = series_response?.tsMetric;
      if (!tsMetric) return;

      const axisId = getMetricAxisId(tsMetric);
      const allNull = seriesIsAllNull(timeSeries, metricUuid);
      const existingAxis = usedUnits.includes(axisId);
      if (existingAxis || allNull) return;

      const displayUnit = tsMetric.unit || "";
      axes.push(
        <YAxis
          key={axisId}
          yAxisId={axisId}
          unit={displayUnit}
          tick={
            <CustomTick unit={displayUnit} fontSize={10} colorZero={false} />
          }
          tickLine={{ stroke: "#E0E0E0", strokeWidth: 1 }}
          axisLine={{ stroke: "#E0E0E0", strokeWidth: 1 }}
          style={{
            fontStyle: "normal",
            fontWeight: "500",
            fill: "black",
            color: "black",
          }}
          orientation="left"
          width={singleYAxisWidth}
          domain={includeZeroYAxis ? [0, 0] : ["dataMin", "dataMax"]}
          type="number"
        />,
      );
      usedUnits.push(axisId);
    });
    return axes;
  };

  const getYAxisMargin = (): number => {
    return getYAxes().length * singleYAxisWidth;
  };

  const getSeriesName = (uuid: string): string => {
    const result = findSeries(uuid, site);
    const tsAssembly = findAssembly(uuid, site);
    return (tsAssembly?.name ?? "Unknown") + ":" + (result?.tsMetric?.name ?? uuid);
  };

  const getSeriesUnits = (uuid: string): string => {
    const result = findSeries(uuid, site);
    return result?.tsMetric?.unit?.trim() ?? "";
  };

  const getLines = () => {
    const lines: React.ReactNode[] = [];
    timeseriesDataKeys.forEach((key, index) => {
      const series_response = findSeries(key, site);
      const tsMetric = series_response?.tsMetric;
      if (!tsMetric) return;

      const axisId = getMetricAxisId(tsMetric);
      if (seriesIsAllNull(timeSeries, key)) return;
      lines.push(
        <Line
          yAxisId={axisId}
          isAnimationActive={animation}
          onAnimationEnd={handleAnimationEnd}
          key={tsMetric.uuid}
          type={useGraphInterpolation ? "monotone" : "linear"}
          dataKey={key}
          stroke={(theme as any).recharts["grapherColors"][index]}
          strokeWidth={2}
          connectNulls={useGraphInterpolation}
          style={{ zIndex: 20 }}
          dot={false}
          animationDuration={100}
        />,
      );
    });
    return lines;
  };

  const getGrid = () => {
    if (getYAxes().length > 1) return null;
    return (
      <CartesianGrid
        strokeDasharray="4 4"
        stroke="#808080"
        strokeOpacity="10%"
        vertical={false}
      />
    );
  };

  const leftMargin = 20;
  const rightMargin = 20;
  const timelineLeftMargin = getYAxisMargin() + leftMargin;
  const timelineLabelWidth = Math.max(0, timelineLeftMargin - 8);

  const noMetricsConfigured = !metricUuids || metricUuids.length === 0;

  const hasRenderableData = useMemo(() => {
    if (!timeseriesDataKeys.length || !timeSeries.length) return false;
    return timeseriesDataKeys.some((key) => !seriesIsAllNull(timeSeries, key));
  }, [timeseriesDataKeys, timeSeries]);

  const missingData =
    noMetricsConfigured ||
    timeSeries.length === 0 ||
    !metricsTimeseriesData ||
    !hasRenderableData;
  const hasError = error || isRefetchError;
  const zoomError = metricUuids?.length > 0 && missingData;

  // Modal handlers
  const handleOpenSettings = () => {
    setModalMetrics([...(configuredMetrics[selectedAssetType] ?? [])]);
    setSettingsOpen(true);
  };

  const handleSaveSettings = (metrics: string[]) => {
    const updatedMetrics = {
      ...configuredMetrics,
      [selectedAssetType]: metrics,
    };
    setConfiguredMetrics(updatedMetrics);

    saveConfigMutation.mutate({
      trace_group_metrics: updatedMetrics,
      interpolation: useGraphInterpolation,
      y_axis_adjust: includeZeroYAxis,
      lock_legend: lockLegend,
    });
  };

  const getToggles = () => (
    <Box sx={{ display: "flex", gap: "10px", ml: "10px" }}>
      <GraphToggle
        value={useGraphInterpolation}
        onChange={() => setUseGraphInterpolation(!useGraphInterpolation)}
        title="Interpolation"
        Icon={PolylineOutlinedIcon}
      />
      <GraphToggle
        value={!includeZeroYAxis}
        onChange={() => setIncludeZeroYAxis(!includeZeroYAxis)}
        title="Y-Axis Scaling"
        Icon={ExpandOutlinedIcon}
      />
      <GraphToggle
        value={lockLegend}
        onChange={() => setLockLegend(!lockLegend)}
        title="Lock Legend"
        Icon={ReadMoreOutlinedIcon}
      />
    </Box>
  );

  const getGraphContent = () => {
    if (isFetching) return <Loading />;

    if (zoomError || hasError) {
      return (
        <TimeSeriesError>
          <Button variant="contained" color="secondary" onClick={refreshGraph}>
            Refresh Graph
          </Button>
        </TimeSeriesError>
      );
    }

    if (missingData) {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            minHeight: "314px",
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {noMetricsConfigured
              ? "Configure data above to view graph of system commands."
              : "No data available for the selected metrics in this time range."}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box display="flex" flexDirection="row" height="400px">
          <Box flexGrow="1" height="100%">
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              height="100%"
              alignItems="stretch"
            >
              {touData?.status === 200 && touData.results?.length > 0 && (
                <TouTimeline
                  intervals={touData.results}
                  displayRange={displayRange}
                  leftMargin={timelineLeftMargin}
                  rightMargin={rightMargin}
                  timezone={timezone}
                />
              )}
              {weatherTimeSeriesData && flags.weatherTimeseries && (
                <Box sx={{ position: "relative", width: "100%" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${Math.max(0, getYAxisMargin() + leftMargin - 8)}px`,
                      paddingRight: "8px",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      Weather
                    </Typography>
                  </Box>
                  <WeatherTimeline
                    data={weatherTimeSeriesData}
                    displayRange={displayRange}
                    rightMargin={rightMargin}
                    leftMargin={leftMargin + getYAxisMargin()}
                    yAxisMargin={singleYAxisWidth}
                    weatherConfig={weatherConfig}
                    timezone={timezone}
                    hideLabel
                    syncId="activity-graph"
                    onMouseMove={handleWeatherHover}
                    onMouseLeave={() => setGraphPayload(null)}
                  />
                </Box>
              )}
              <Box sx={{ width: "100%", flex: 1, position: "relative" }}>
                <ResponsiveContainer width="99%" height="99%">
                  <ComposedChart
                    syncId="activity-graph"
                    syncMethod={(_ticks: unknown, hoverData: any) => {
                      if (
                        hoverData.activeLabel === hoverData.activeTooltipIndex
                      ) {
                        return hoverData.activeTooltipIndex;
                      }
                      return GraphUtil.findIndexByClosestUnixTime(
                        timeSeries,
                        Number(hoverData.activeLabel),
                      );
                    }}
                    data={timeSeries}
                    margin={{
                      top: 5,
                      right: rightMargin,
                      left: leftMargin,
                      bottom: 15,
                    }}
                    onMouseDown={(ev: any) =>
                      GraphUtil.handleZoomStart(
                        ev,
                        zoomParams,
                        setZoomParams,
                        displayRange,
                      )
                    }
                    onMouseUp={(ev: any) => {
                      const newRange = GraphUtil.handleZoomEnd(
                        ev,
                        zoomParams,
                        setZoomParams,
                        timezone,
                      );
                      setPreZoomDisplayRange(displayRange);
                      setDisplayRange(GraphUtil.updateDisplayRange(newRange));
                    }}
                    onMouseMove={(ev: any) => {
                      GraphUtil.handleZoomUpdate(ev, zoomParams, setZoomParams);
                      setGraphPayload(ev.activePayload);
                    }}
                    onMouseLeave={() => setGraphPayload(null)}
                  >
                    <CustomXAxis hide={false} />
                    <RechartsTooltip
                      wrapperStyle={{ zIndex: 10000 }}
                      content={
                        lockLegend ? (
                          <></>
                        ) : (
                          <LineGraphTooltip
                            getBucketAlarms={getBucketAlarms}
                            timezone={timezone}
                            getName={(uuid: string) => getSeriesName(uuid)}
                            getUnit={(uuid: string) => getSeriesUnits(uuid)}
                            showAlarms={true}
                            metricUuids={metricUuids}
                            graphPayload={graphPayload as Record<string, unknown>[] | null}
                            sortMetrics={false}
                            colorPaletteKey="grapherColors"
                            getWeatherTooltipItems={getWeatherTooltipItems}
                            getActiveTouItem={getActiveTouItem}
                          />
                        )
                      }
                      filterNull={false}
                    />
                    {getLines()}
                    {getYAxes()}
                    {getGrid()}
                    {zoomParams?.zooming && axisUnits.length > 0 ? (
                      <ReferenceArea
                        yAxisId={axisUnits[0].id}
                        x1={zoomParams.startIndex}
                        x2={zoomParams.stopIndex}
                      />
                    ) : null}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: `${timelineLabelWidth}px`,
                    paddingRight: "8px",
                    boxSizing: "border-box",
                    textAlign: "right",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "20px",
                    mt: "5px",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    Alarms
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    paddingRight: `${rightMargin}px`,
                    boxSizing: "border-box",
                  }}
                >
                  <AlarmTimeline
                    timeSeries={timeSeries}
                    displayRange={displayRange}
                    bigBuckets={bigBuckets}
                    alarmHistoryData={alarmHistoryData}
                    getBucketAlarms={getBucketAlarms}
                    timezone={timezone}
                    leftMargin={0}
                    rightMargin={0}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          {lockLegend && (
            <Box>
              <LineGraphTooltip
                getBucketAlarms={getBucketAlarms}
                timezone={timezone}
                getName={(uuid: string) => getSeriesName(uuid)}
                getUnit={(uuid: string) => getSeriesUnits(uuid)}
                showAlarms={true}
                metricUuids={metricUuids}
                isStaticLegend={true}
                graphPayload={graphPayload as Record<string, unknown>[] | null}
                sortMetrics={false}
                colorPaletteKey="grapherColors"
                getWeatherTooltipItems={getWeatherTooltipItems}
                getActiveTouItem={getActiveTouItem}
                staticTouItems={staticTouItems}
              />
            </Box>
          )}
        </Box>
      </>
    );
  };

  return (
    <Box display="flex" flexDirection="column" sx={(theme as any).classes.genericPanel}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Activity
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ mb: 3 }}>
        Automated Commands
      </Typography>

      {/* Controls row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DateOptionPicker
            site={site}
            updateDisplayRange={(range: any) => {
              setDisplayRange(GraphUtil.updateDisplayRange(range));
              setZoomParams({ zooming: false, zoomed: false });
            }}
            range={displayRange.range}
            include24Hours
          />
          {!missingData && getToggles()}
          {zoomParams.zoomed && (
            <Button
              variant="outlined"
              size="small"
              color="alt"
              onClick={cancelZoom}
              sx={{ ml: ".75rem" }}
            >
              Cancel Zoom
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="trace-group-label">Trace Groups</InputLabel>
              <Select
                labelId="trace-group-label"
                value={selectedAssetType}
                label="Trace Groups"
                onChange={(e) => setSelectedAssetType(e.target.value)}
              >
                {availableTraceGroups.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip
              title={
                !isAdmin
                  ? "Only admins can configure metrics"
                  : selectedAssetType === "All"
                    ? "Select a specific trace group to configure"
                    : "Configure metrics for this trace group"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenSettings}
                  disabled={!isAdmin || selectedAssetType === "All"}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    width: 40,
                    height: 40,
                  }}
                >
                  <SettingsOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <GraphDownloadMenu
            displayRange={displayRange}
            site={site}
            timezone={timezone}
            metricsUuids={metricUuids}
          />
        </Box>
      </Box>

      {/* Graph area */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          minHeight: "480px",
        }}
      >
        {getGraphContent()}
      </Box>

      {/* Configure Data Modal */}
      <ConfigureDataModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSave={handleSaveSettings}
        site={site}
        assetTypeLabel={getTraceGroupLabel(selectedAssetType)}
        selectedMetricUuids={modalMetrics}
        onMetricsChange={setModalMetrics}
        isSaving={saveConfigMutation.isPending}
      />
    </Box>
  );
};

export default ActivityTab;
